// @ts-nocheck
/**
 * Command Center Module Configuration
 *
 * Central dashboard for fleet monitoring, alerts, and AI insights.
 * Adapts terminology based on industry (Equipment/Device/Vehicle Fleet).
 */

import { ModuleConfig } from './moduleTypes'

export const commandCenterConfig: ModuleConfig = {
  id: 'command-center',
  name: 'Command Center',
  description: 'Real-time fleet monitoring and AI-powered insights',
  icon: 'LayoutDashboard',
  basePath: '/',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'overview',
      path: '',
      name: 'Fleet Overview',
      icon: 'Gauge',
      description: 'Real-time fleet health status and key metrics',
      component: 'FleetOverview',
      requiredPermissions: ['assets:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Device Fleet Overview',
        financial: 'System Portfolio Overview',
        transportation: 'Vehicle Fleet Overview',
        iot_telecom: 'SIM Fleet Dashboard',
      },
      industryDescriptions: {
        healthcare: 'Real-time medical device fleet status and metrics',
        financial: 'System health and portfolio performance overview',
        transportation: 'Vehicle fleet status and route metrics',
        iot_telecom: 'Real-time SIM connectivity and network health',
      },
    },
    {
      id: 'alerts',
      path: 'alerts',
      name: 'Critical Alerts',
      icon: 'AlertTriangle',
      description: 'Priority action items requiring immediate attention',
      component: 'CriticalAlertsDashboard',
      requiredPermissions: ['alerts:read'],
      position: 2,
      industryLabels: {
        healthcare: 'Device Alerts',
        financial: 'Risk Alerts',
        transportation: 'Fleet Alerts',
        iot_telecom: 'Network Alerts',
      },
      badge: {
        type: 'count',
        dataKey: 'criticalAlertCount',
      },
    },
    {
      id: 'intelligence',
      path: 'intelligence',
      name: 'AI Intelligence',
      icon: 'Brain',
      description: 'Deep analysis and AI-generated recommendations',
      component: 'FleetIntelligencePanel',
      requiredPermissions: ['analytics:read'],
      position: 3,
      industryLabels: {
        healthcare: 'Clinical AI Insights',
        financial: 'Risk Intelligence',
        transportation: 'Fleet Intelligence',
        iot_telecom: 'Network Intelligence',
      },
    },
    {
      id: 'actions',
      path: 'actions',
      name: 'Action Queue',
      icon: 'ListTodo',
      description: 'Prioritized list of recommended actions',
      component: 'ActionQueuePage',
      requiredPermissions: ['assets:write'],
      position: 4,
      industryLabels: {
        healthcare: 'Service Queue',
        financial: 'Action Items',
        transportation: 'Service Queue',
      },
    },
  ],

  workflows: [
    {
      id: 'critical-response',
      name: 'Critical Alert Response',
      description: 'Step-by-step guide for handling critical alerts',
      icon: 'AlertOctagon',
      steps: [
        {
          id: 'assess',
          name: 'Assess Severity',
          description: 'Review alert details, affected assets, and potential impact',
          icon: 'Search',
          estimatedDuration: 5,
        },
        {
          id: 'notify',
          name: 'Notify Stakeholders',
          description: 'Alert relevant personnel based on severity and type',
          icon: 'Bell',
          estimatedDuration: 3,
          industryLabels: {
            healthcare: 'Notify Clinical Team',
            financial: 'Notify Risk Team',
          },
        },
        {
          id: 'investigate',
          name: 'Root Cause Analysis',
          description: 'Navigate to root cause module for detailed analysis',
          navigateTo: '/root-cause',
          icon: 'Microscope',
          estimatedDuration: 15,
        },
        {
          id: 'action',
          name: 'Take Action',
          description: 'Schedule maintenance or implement corrective measures',
          navigateTo: '/maintenance',
          icon: 'Wrench',
          estimatedDuration: 10,
          industryLabels: {
            healthcare: 'Schedule Service',
            transportation: 'Dispatch Service',
          },
        },
        {
          id: 'verify',
          name: 'Verify Resolution',
          description: 'Confirm the issue is resolved and document findings',
          icon: 'CheckCircle',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['criticalAlertReceived', 'multipleWarningsOnAsset'],
    },
    {
      id: 'daily-review',
      name: 'Daily Fleet Review',
      description: 'Systematic review of fleet health and priorities',
      icon: 'Calendar',
      steps: [
        {
          id: 'health-check',
          name: 'Fleet Health Check',
          description: 'Review overall fleet health score and trends',
          icon: 'Activity',
          estimatedDuration: 5,
        },
        {
          id: 'alert-triage',
          name: 'Alert Triage',
          description: 'Review and prioritize overnight alerts',
          navigateTo: '/alerts',
          icon: 'Filter',
          estimatedDuration: 10,
        },
        {
          id: 'predictions-review',
          name: 'Review Predictions',
          description: 'Check upcoming predicted failures and risks',
          navigateTo: '/predictions',
          icon: 'TrendingUp',
          estimatedDuration: 10,
        },
        {
          id: 'action-planning',
          name: 'Plan Actions',
          description: 'Schedule maintenance and assign tasks',
          navigateTo: '/actions',
          icon: 'ClipboardList',
          estimatedDuration: 15,
        },
      ],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'situation',
        name: 'Current Situation',
        template: 'Your {facilityTerm} has {totalAssets} {assetPluralTerm} with an overall {healthTerm} score of {healthScore}%. {criticalCount} are in critical condition, and {warningCount} require attention.',
        order: 1,
        required: true,
      },
      {
        id: 'analysis',
        name: 'Analysis',
        template: 'Fleet {healthTerm} is {trendDirection} compared to the previous period. {anomalyCount} anomalies have been detected today. Primary concerns center on {topConcernArea}.',
        order: 2,
        condition: 'hasAnomalies',
      },
      {
        id: 'risk',
        name: 'Risk Assessment',
        template: 'Without intervention, there is a {failureProbability}% probability of {failureTerm} within {timeToFailure}. Estimated potential impact: {potentialImpact}.',
        order: 3,
        condition: 'hasCriticalAssets',
      },
      {
        id: 'protective',
        name: 'Protective Measures',
        template: '**Protective Measures:**\n• {primaryAction}\n• {secondaryAction}\n• Continue monitoring {monitoringTarget}',
        order: 4,
        required: true,
      },
      {
        id: 'priority-actions',
        name: 'Priority Actions',
        template: '**Priority Actions:**\n1. {action1} - {action1Urgency}\n2. {action2} - {action2Urgency}\n3. {action3} - {action3Urgency}',
        order: 5,
        required: true,
      },
    ],
    availableVariables: [
      'facilityTerm', 'totalAssets', 'assetPluralTerm', 'healthTerm', 'healthScore',
      'criticalCount', 'warningCount', 'trendDirection', 'anomalyCount', 'topConcernArea',
      'failureProbability', 'failureTerm', 'timeToFailure', 'potentialImpact',
      'primaryAction', 'secondaryAction', 'monitoringTarget',
      'action1', 'action1Urgency', 'action2', 'action2Urgency', 'action3', 'action3Urgency',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Device Command Center',
      description: 'Medical device fleet monitoring and clinical insights',
      additionalSubPages: [
        {
          id: 'compliance',
          path: 'compliance',
          name: 'Compliance Status',
          icon: 'Shield',
          description: 'Regulatory compliance and calibration status',
          component: 'ComplianceStatusPage',
          position: 5,
        },
      ],
    },
    financial: {
      name: 'Operations Center',
      description: 'System portfolio monitoring and risk intelligence',
      icon: 'Building2',
    },
    transportation: {
      name: 'Fleet Command Center',
      description: 'Vehicle fleet monitoring and route intelligence',
      additionalSubPages: [
        {
          id: 'routes',
          path: 'routes',
          name: 'Route Status',
          icon: 'Map',
          description: 'Active routes and delivery status',
          component: 'RouteStatusPage',
          position: 5,
        },
      ],
    },
    iot_telecom: {
      name: 'SIM Fleet Command Center',
      description: 'Global IoT SIM connectivity monitoring and network optimization',
      icon: 'Radio',
      additionalSubPages: [
        {
          id: 'network-optimization',
          path: 'network-optimization',
          name: 'Network Optimization',
          icon: 'Wifi',
          description: 'AI-powered best network selection and carrier analysis',
          component: 'NetworkOptimizationPage',
          position: 5,
        },
        {
          id: 'coverage-map',
          path: 'coverage-map',
          name: 'Coverage Analysis',
          icon: 'MapPin',
          description: 'Global coverage visualization and carrier comparison',
          component: 'CoverageMapPage',
          position: 6,
        },
        {
          id: 'carrier-performance',
          path: 'carrier-performance',
          name: 'Carrier Performance',
          icon: 'BarChart2',
          description: 'Carrier KPIs, benchmarks, and cost analysis',
          component: 'CarrierPerformancePage',
          position: 7,
        },
        {
          id: 'compliance',
          path: 'compliance',
          name: 'Regulatory Compliance',
          icon: 'Shield',
          description: 'Regional compliance status and local IMSI requirements',
          component: 'TelecomCompliancePage',
          position: 8,
        },
      ],
      additionalWorkflows: [
        {
          id: 'best-network-selection',
          name: 'Best Network Selection (AI-Recommended)',
          description: 'AI-assisted workflow to identify optimal carrier for each SIM based on signal quality, coverage, and cost analysis',
          icon: 'Signal',
          triggerConditions: [
            'Poor signal quality detected (RSSI < -90 dBm or RSRP < -100 dBm)',
            'High data costs on current carrier',
            'New SIM deployment in region',
            'Carrier performance degradation trend detected',
            'Multi-IMSI profile available but underutilized'
          ],
          sopReference: 'SOP-IOT-003',
          estimatedTotalDuration: 28,
          steps: [
            {
              id: 'assess-requirements',
              name: 'Assess Requirements',
              description: 'Review SIM location, data needs, and latency requirements',
              icon: 'ClipboardList',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-coverage',
              name: 'Analyze Coverage',
              description: 'Compare available carrier coverage at device location',
              navigateTo: '/coverage-map',
              icon: 'Map',
              estimatedDuration: 5,
            },
            {
              id: 'benchmark-carriers',
              name: 'Benchmark Carriers',
              description: 'Review carrier performance metrics and costs',
              navigateTo: '/carrier-performance',
              icon: 'BarChart2',
              estimatedDuration: 10,
            },
            {
              id: 'check-compliance',
              name: 'Verify Compliance',
              description: 'Ensure carrier meets regional regulatory requirements',
              navigateTo: '/compliance',
              icon: 'Shield',
              estimatedDuration: 3,
            },
            {
              id: 'switch-profile',
              name: 'Execute Profile Switch',
              description: 'Initiate OTA profile switch to optimal carrier',
              icon: 'RefreshCw',
              estimatedDuration: 5,
            },
            {
              id: 'verify-connectivity',
              name: 'Verify Connectivity',
              description: 'Confirm successful connection and baseline metrics',
              icon: 'CheckCircle',
              estimatedDuration: 5,
            },
          ],
        },
        {
          id: 'connectivity-troubleshooting',
          name: 'Connectivity Troubleshooting (SOP-IOT-001)',
          description: 'Systematic troubleshooting for offline or degraded SIMs following industry-standard diagnostic procedures',
          icon: 'Wrench',
          sopReference: 'SOP-IOT-001',
          estimatedTotalDuration: 22,
          triggerConditions: [
            'SIM offline or disconnected',
            'Weak signal alerts (RSSI < -100 dBm)',
            'High latency detected (>500ms)',
            'Connection drops or frequent reconnects',
            'Data session failures',
            'Signal quality below threshold (RSRQ < -15 dB or SINR < 0 dB)'
          ],
          steps: [
            {
              id: 'check-status',
              name: 'Check SIM Status',
              description: 'Verify SIM activation status and account standing',
              icon: 'CreditCard',
              estimatedDuration: 2,
            },
            {
              id: 'run-diagnostics',
              name: 'Run Remote Diagnostics',
              description: 'Execute OTA diagnostic commands',
              icon: 'Activity',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-signal',
              name: 'Analyze Signal History',
              description: 'Review RSSI/RSRP trends and identify degradation patterns',
              navigateTo: '/assets',
              icon: 'TrendingDown',
              estimatedDuration: 10,
            },
            {
              id: 'try-profile-switch',
              name: 'Try Profile Switch',
              description: 'Attempt connection via alternate carrier profile',
              icon: 'RefreshCw',
              estimatedDuration: 5,
            },
            {
              id: 'escalate-or-resolve',
              name: 'Escalate or Resolve',
              description: 'Create field service ticket if remote resolution fails',
              icon: 'ArrowUpRight',
              estimatedDuration: 5,
            },
          ],
        },
      ],
    },
    datacenter: {
      name: 'Infrastructure Command Center',
      description: 'Server fleet monitoring, cooling systems, and power infrastructure',
      icon: 'Server',
    },
  },

  requiredPermissions: ['dashboard:read'],
}
